import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  DollarSign,
  ArrowLeft,
  ChevronRight
} from "lucide-react";
import { useApplicationSettingsContext } from "../contexts/ApplicationSettingsContext";
import CoreOperatingCosts from "./settings/CoreOperatingCosts";
import PermissionGuard from '../components/PermissionGuard';

// Organize settings in categories
const settingsCategories = [
  {
    id: "configure",
    label: "Configure",
    items: [
      { id: "operating-costs", label: "Operating Costs", icon: DollarSign, component: CoreOperatingCosts },
    ]
  },
];

export default function Settings() {
  const navigate = useNavigate();
  const location = useLocation();
  const { disabledPages } = useApplicationSettingsContext();
  
  // Check if we were redirected from /online-bookings
  const wasRedirectedFromOnlineBookings = location.state && 
    location.state?.from === '/online-bookings';

  // Filter out disabled settings pages
  const filteredSettingsCategories = settingsCategories.map(category => ({
    ...category,
    items: category.items.filter(item => !disabledPages.includes(item.id as any))
  })).filter(category => category.items.length > 0);

  // Flatten tabs for component lookup (filtered)
  const tabs = filteredSettingsCategories.flatMap(category => category.items);

  const [activeTab, setActiveTab] = useState(wasRedirectedFromOnlineBookings ? 'online-bookings' : tabs.length > 0 ? tabs[0].id : 'operating-costs');
  const [showSettingsList, setShowSettingsList] = useState(true);

  const ActiveComponent =
    tabs.find((tab) => tab.id === activeTab)?.component || CoreOperatingCosts;
  const activeTabLabel =
    tabs.find((tab) => tab.id === activeTab)?.label || "Operating Costs";

  useEffect(() => {
    if (location.state && location.state.from === '/online-bookings') {
      setActiveTab('online-bookings');
      setShowSettingsList(false);
    }
    
    if (location.state && location.state.activeTab) {
      setActiveTab(location.state.activeTab);
      setShowSettingsList(false);
    }
    
    if (disabledPages.includes(activeTab as any) && tabs.length > 0) {
      setActiveTab(tabs[0].id);
      setShowSettingsList(false);
    }
  }, [location.state, location.pathname, location.search, activeTab, disabledPages, tabs]);

  const shouldShowList = showSettingsList || tabs.length === 0;

  return (
    <PermissionGuard permission="settings.pageAccess">
      <div className="min-h-screen bg-gray-50" style={{fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif'}}>
        {/* Header */}
        <div className="bg-white border-b border-gray-200/60 sticky top-0 z-10 px-0 sm:px-4 py-3">
          <div className="flex items-center gap-3 px-4 sm:px-0">
            {!shouldShowList ? (
              <button
                onClick={() => setShowSettingsList(true)}
                className="p-2 -ml-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors touch-manipulation"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
            ) : (
              <button
                onClick={() => navigate(-1)}
                className="p-2 -ml-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors touch-manipulation"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
            )}
            <div className="flex-1">
              <h1 className="text-xl font-semibold text-gray-900">
                {shouldShowList ? 'Settings' : activeTabLabel}
              </h1>
              {shouldShowList && (
                <p className="text-sm text-gray-500 mt-0.5">Manage your business settings</p>
              )}
            </div>
            {!shouldShowList && (
              <button
                onClick={() => setShowSettingsList(true)}
                className="text-sm font-medium text-blue-600 hover:text-blue-700 px-3 py-1.5 rounded-lg hover:bg-blue-50 transition-colors"
              >
                All Settings
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        {shouldShowList ? (
          <div className="px-0 sm:px-4 py-4 space-y-4">
            {filteredSettingsCategories.map((category) => (
              <div key={category.id} className="space-y-2">
                <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 sm:px-1">
                  {category.label}
                </h2>
                
                <div className="bg-white border-x-0 sm:border-x border-t border-b border-gray-200 sm:rounded-xl overflow-hidden">
                  {category.items.map((tab, index) => {
                    const Icon = tab.icon;
                    return (
                      <div key={tab.id}>
                        <button
                          onClick={() => {
                            setActiveTab(tab.id);
                            setShowSettingsList(false);
                          }}
                          className="w-full flex items-center justify-between px-4 py-4 hover:bg-gray-50 active:bg-gray-50 transition-colors touch-manipulation text-left"
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                              <Icon className="h-5 w-5 text-blue-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="text-base font-semibold text-gray-900">{tab.label}</h3>
                            </div>
                          </div>
                          <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0 ml-2" />
                        </button>
                        {index < category.items.length - 1 && (
                          <div className="border-t border-gray-200/60" />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="px-0 sm:px-4 py-4">
            <ActiveComponent />
          </div>
        )}
      </div>
    </PermissionGuard>
  );
}